import { create } from "zustand";
import type { WorkspaceSeed, Currency, MaterialityConfig } from "./types";
import { DEFAULT_MATERIALITY } from "./types";
import { DEFAULT_WORKSPACE_ID } from "./seed/workspaces";
import { profiles, devProfile } from "./seed/profiles";

/**
 * Canonical workspace seeds, fetched once from GET /api/workspace-seed
 * (SeedProvider) and kept here so resetWorkspaceData can restore a pristine
 * copy without re-fetching. The working copies in the store are deep clones.
 */
let canonicalSeeds: Record<string, WorkspaceSeed> = {};

const PROFILE_KEY = "il.activeProfileId";
const WORKSPACE_KEY = "il.activeWorkspaceId";
const DEVMODE_KEY = "il.devMode";
const CURRENCY_KEY = "il.displayCurrency";
const SYNC_WIFI_ONLY_KEY = "il.syncOnlyOnWifi";

function readLS(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function writeLS(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

const DEFAULT_PROFILE_ID = profiles[0].id;

export interface StoreState {
  activeProfileId: string;
  activeWorkspaceId: string;
  devMode: boolean;
  /** Reporter PWA "simulate offline" toggle - a QA convenience layered on
   * top of real connectivity detection (useOnlineStatus), not a replacement
   * for it. The actual offline mutation queue lives in offlineQueue.ts /
   * offlineDb.ts (IndexedDB), not here. */
  reporterOffline: boolean;
  /** Reporter setting: pause *automatic* offline-queue sync on a detected cellular
   * connection, to protect a limited/expensive data plan. Defaults off - most field
   * sites here run on mobile data, not Wi-Fi, so automatic sync on any connection is
   * the correct default. Never gates a manual "Sync now" (offlineQueue.ts's flushQueue
   * itself stays unconditional; only initOfflineSync's automatic triggers check this). */
  syncOnlyOnWifi: boolean;
  /** Preferred display currency; any figure can be flipped to its source (spec §2.6). */
  displayCurrency: Currency;
  /** Oversight materiality thresholds (v3 §Chunk 5). */
  materiality: MaterialityConfig;
  /** Mutable working copy of all workspace seeds. */
  seeds: Record<string, WorkspaceSeed>;
  /** True once the seeds have been loaded from the API (SeedProvider). */
  seedsHydrated: boolean;

  /** Install the canonical seeds fetched from the API (called once at boot). */
  hydrateSeeds: (seedsById: Record<string, WorkspaceSeed>) => void;
  setProfile: (id: string) => void;
  setWorkspace: (id: string) => void;
  setDisplayCurrency: (c: Currency) => void;
  setMateriality: (patch: Partial<MaterialityConfig>) => void;
  toggleDevMode: () => void;
  setDevMode: (v: boolean) => void;
  toggleReporterOffline: () => void;
  toggleSyncOnlyOnWifi: () => void;
  resetWorkspaceData: () => void;

  /** Mutate the active workspace seed in place (immutably swaps the object). */
  updateActiveSeed: (updater: (seed: WorkspaceSeed) => WorkspaceSeed) => void;
  /** Mutate a specific workspace seed by id (for cross-workspace actions like sharing a programme). */
  updateSeedById: (workspaceId: string, updater: (seed: WorkspaceSeed) => WorkspaceSeed) => void;
}

export const useStore = create<StoreState>((set, get) => ({
  activeProfileId: readLS(PROFILE_KEY, DEFAULT_PROFILE_ID),
  activeWorkspaceId: readLS(WORKSPACE_KEY, DEFAULT_WORKSPACE_ID),
  devMode: readLS(DEVMODE_KEY, "false") === "true",
  reporterOffline: false,
  syncOnlyOnWifi: readLS(SYNC_WIFI_ONLY_KEY, "false") === "true",
  displayCurrency: readLS(CURRENCY_KEY, "UGX") as Currency,
  materiality: DEFAULT_MATERIALITY,
  seeds: {},
  seedsHydrated: false,

  hydrateSeeds: (seedsById) => {
    canonicalSeeds = seedsById;
    set({ seeds: structuredClone(canonicalSeeds), seedsHydrated: true });
  },
  setProfile: (id) => {
    writeLS(PROFILE_KEY, id);
    // Default the display currency to the persona's own currency on entry so funder
    // figures land in EUR (their commitments) and field/oversight personas in UGX
    // (the ledger base). The CurrencyToggle still lets either flip afterwards; this
    // only sets the landing value, and is the single source of the funder's currency.
    const next = profiles.find((p) => p.id === id);
    const personaCurrency: Currency = next?.group === "funder" ? "EUR" : "UGX";
    writeLS(CURRENCY_KEY, personaCurrency);
    set({ activeProfileId: id, displayCurrency: personaCurrency });
  },
  setWorkspace: (id) => {
    writeLS(WORKSPACE_KEY, id);
    set({ activeWorkspaceId: id });
  },
  setDisplayCurrency: (c) => {
    writeLS(CURRENCY_KEY, c);
    set({ displayCurrency: c });
  },
  setMateriality: (patch) => set((s) => ({ materiality: { ...s.materiality, ...patch } })),
  toggleDevMode: () => {
    const next = !get().devMode;
    writeLS(DEVMODE_KEY, String(next));
    set({ devMode: next });
  },
  setDevMode: (v) => {
    writeLS(DEVMODE_KEY, String(v));
    set({ devMode: v });
  },
  toggleReporterOffline: () => set((s) => ({ reporterOffline: !s.reporterOffline })),
  toggleSyncOnlyOnWifi: () => {
    const next = !get().syncOnlyOnWifi;
    writeLS(SYNC_WIFI_ONLY_KEY, String(next));
    set({ syncOnlyOnWifi: next });
  },
  resetWorkspaceData: () => set({ seeds: structuredClone(canonicalSeeds) }),

  updateActiveSeed: (updater) =>
    set((state) => {
      const id = state.activeWorkspaceId;
      const current = state.seeds[id];
      if (!current) return state;
      return { seeds: { ...state.seeds, [id]: updater(current) } };
    }),

  updateSeedById: (workspaceId, updater) =>
    set((state) => {
      const current = state.seeds[workspaceId];
      if (!current) return state;
      return { seeds: { ...state.seeds, [workspaceId]: updater(current) } };
    }),
}));

/** All selectable profiles, including the dev profile when dev mode is on. */
export function useSelectableProfiles() {
  const devMode = useStore((s) => s.devMode);
  return devMode ? [...profiles, devProfile] : profiles;
}
